import { useCallback, useMemo } from "react";
import { useRecoilValue } from "recoil";

import { resolveGridPermissionEntity } from "../helpers";
import {
  IReadOrUpdatePermission,
  RydeUiUnit,
  TPermissionAction,
} from "../models";
import { entitiesAtom, pageEntityAtom } from "../recoil";
import { useHasPermissionsCheck } from "./use-has-permissions-check";

interface IFieldPermissions {
  readPermission?: IReadOrUpdatePermission | null;
  updatePermission?: IReadOrUpdatePermission | null;
  formEntity?: RydeUiUnit;
}

interface IFieldPermissionsCheckResult {
  isFieldVisible: boolean;
  isFieldDisabled: boolean;
}

export const useHasPermissionCustomField = () => {
  const { hasPermissionsCheck } = useHasPermissionsCheck();
  const currentPageEntity = useRecoilValue(pageEntityAtom);
  const entities = useRecoilValue(entitiesAtom);

  const entityMapping = useMemo(
    () =>
      new Map(entities?.map(({ id, name }) => [id, name as RydeUiUnit]) ?? []),
    [entities],
  );

  const doesPermissionExist = useCallback(
    (permission: TPermissionAction, entity: RydeUiUnit) =>
      hasPermissionsCheck({ entity, action: permission }),
    [hasPermissionsCheck],
  );

  const getFieldPermissions = useCallback(
    ({
      readPermission,
      updatePermission,
      formEntity,
    }: IFieldPermissions): IFieldPermissionsCheckResult => {
      const readEntity =
        readPermission?.entityId && entityMapping.get(readPermission.entityId);
      const updateEntity =
        updatePermission?.entityId &&
        entityMapping.get(updatePermission.entityId);

      const isFieldVisible =
        readPermission?.action && readEntity
          ? doesPermissionExist(readPermission.action, readEntity)
          : true;

      const permissionEntity =
        formEntity ?? resolveGridPermissionEntity(currentPageEntity);

      const isFieldDisabled =
        updatePermission?.action && updateEntity
          ? !doesPermissionExist(updatePermission.action, updateEntity)
          : !doesPermissionExist("update", permissionEntity);

      return {
        isFieldVisible,
        isFieldDisabled,
      };
    },
    [currentPageEntity, doesPermissionExist, entityMapping],
  );
  return { getFieldPermissions };
};
